import { createRouter, createWebHistory } from 'vue-router';
import { useAppStore } from '@/stores/app';
import { useAudioStore } from '@/stores/audio';

const routes = [
    {
        path: '/',
        name: 'home',
        component: () => import('@/pages/Home.vue'),
        meta: {
            title: 'Home - Windorchestra',
            description: 'Welcome to the Windorchestra website, your source for free sheet music, photos, and learning resources.'
        }
    },
    {
        path: '/resources',
        name: 'resources',
        component: () => import('@/pages/Resources.vue'),
        meta: {
            title: 'Resources - Windorchestra'
        }
    },
    {
        path: '/resources/:slug',
        name: 'resources-detail',
        component: () => import('@/pages/ResourceDisplay.vue'),
        props: true,
        meta: { title: 'Sheet Music - Windorchestra' }
    },
    {
        path: '/photos',
        name: 'photos',
        component: () => import('@/pages/Photos.vue'),
        meta: {
            title: 'Photos - Windorchestra',
            description: 'Photo albums from our events and concerts.'
        }
    },
    {
        path: '/photos/:id',
        name: 'photo-album',
        component: () => import('@/pages/Gallery.vue'),
        props: true,
        meta: { title: 'Photo Album - Windorchestra' }
    },
    {
        path: '/prazdniktrub',
        name: 'prazdniktrub',
        component: () => import('@/pages/prazdniktrub/Index.vue'),
        meta: { title: 'Prazdnik Trub - Windorchestra' }
    },
    {
        path: '/prazdniktrub/2005',
        name: 'trub2005',
        component: () => import('@/pages/prazdniktrub/Year2005.vue'),
        meta: { title: 'Prazdnik Trub 2005 - Windorchestra' }
    },
    {
        path: '/prazdniktrub/2005-mp3',
        name: 'trub2005-mp3',
        component: () => import('@/pages/prazdniktrub/Year2005Mp3.vue'),
        meta: { title: 'Prazdnik Trub 2005 MP3 - Windorchestra' }
    },
    {
        path: '/prazdniktrub/2006',
        name: 'trub2006',
        component: () => import('@/pages/prazdniktrub/Year2006.vue'),
        meta: { title: 'Prazdnik Trub 2006 - Windorchestra' }
    },
    {
        path: '/prazdniktrub/2007',
        name: 'trub2007',
        component: () => import('@/pages/prazdniktrub/Year2007.vue'),
        meta: { title: 'Prazdnik Trub 2007 - Windorchestra' }
    },
    {
        path: '/prazdniktrub/2008',
        name: 'trub2008',
        component: () => import('@/pages/prazdniktrub/Year2008.vue'),
        meta: { title: 'Prazdnik Trub 2008 - Windorchestra' }
    },
    {
        path: '/prazdniktrub/2011',
        name: 'trub2011',
        component: () => import('@/pages/prazdniktrub/Year2011.vue'),
        meta: { title: 'Prazdnik Trub 2011 - Windorchestra' }
    },
    {
        path: '/links',
        name: 'links',
        component: () => import('@/pages/Links.vue'),
        meta: {
            title: 'Links - Windorchestra',
            description: 'Useful links and resources for musicians.'
        }
    },
    {
        path: '/contact',
        name: 'contact',
        component: () => import('@/pages/Contact.vue'),
        meta: {
            title: 'Contact - Windorchestra',
            description: 'Get in touch with us for questions or comments.'
        }
    },

    // Legacy Redirects
    { path: '/sheetmusic.php', redirect: '/resources' },
    { path: '/sheetMusic.php', redirect: '/resources' },
    { path: '/material-orchestra', redirect: '/resources' },
    { path: '/material/orchestra', redirect: '/resources' },
    { path: '/sheetmusic.html', redirect: '/resources' },
    { path: '/sheetmusic', redirect: '/resources' },
    { path: '/sheetMusic', redirect: '/resources' },
    { path: '/material-choir', redirect: '/resources' },
    { path: '/material/choir', redirect: '/resources' },
    { path: '/material-learning', redirect: '/resources' },
    { path: '/material/learning', redirect: '/resources' },
    { path: '/photos.php', redirect: '/photos' },
    { path: '/orchestras', redirect: '/resources' },
    { path: '/orchestras.php', redirect: '/resources' },
    { path: '/links.php', redirect: '/links' },
    { path: '/programs.php', redirect: '/' }, // Or a new /programs page
    { path: '/other/programs.php', redirect: '/' },
    { path: '/index.html', redirect: '/' },
    { path: '/index', redirect: '/' },
    { path: '/material/display/:id', redirect: to => ({ name: 'resources-detail', params: { slug: to.params.id }})},
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition;
        if (to.hash) return { el: to.hash, behavior: 'smooth' };
        return { top: 0, behavior: 'smooth' };
    }
});

// Navigation guards
router.beforeEach((to, from, next) => {
    const appStore = useAppStore();
    const audioStore = useAudioStore();

    // Stop any playing audio when navigating
    audioStore.stop();

    appStore.setLoading(true);

    const descriptionTag = document.querySelector('meta[name="description"]');

    if (to.meta.title) {
        document.title = to.meta.title;
    }
    if (to.meta.description && descriptionTag) {
        descriptionTag.setAttribute('content', to.meta.description);
    }

    next();
});

router.afterEach(() => {
    const appStore = useAppStore();
    appStore.setLoading(false);
});

export default router;
